/**
 * WEEKLY GOALS — pure calculation of "how much of my weekly tracked-hours target
 * have I met so far?".
 *
 * The target itself is a LOCAL preference (device-scoped localStorage, same class
 * as the timesheet rate fallback), so it lives outside PowerSync. Everything here
 * is PURE and deterministic: the caller feeds the current time entries + `now` and
 * gets back the progress snapshot the dashboard renders. No I/O, no Date.now()
 * hidden reads (except the default `now` argument), so it is fully unit-testable.
 *
 * The week window is computed in UTC (Monday-start by default) to stay
 * deterministic regardless of the runner/device timezone and to line up with the
 * UTC ISO timestamps PowerSync stores. Overlap is clipped to the window AND to
 * `now`, so a still-running block only ever contributes the time already elapsed.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const PERCENT_MAX = 100;

const SECONDS_PER_HOUR = MINUTES_PER_HOUR * SECONDS_PER_MINUTE;
const MS_PER_HOUR = SECONDS_PER_HOUR * MS_PER_SECOND;
const MS_PER_DAY = HOURS_PER_DAY * MS_PER_HOUR;

/** Upper bound for a weekly goal: a full 24×7 week. */
export const MAX_WEEKLY_GOAL_HOURS = HOURS_PER_DAY * DAYS_PER_WEEK;

/** localStorage key holding the device-scoped weekly goal (hours, as a string). */
export const WEEKLY_GOAL_STORAGE_KEY = "weekly_goal_hours";

/** Day the week starts on: 0=Sunday … 6=Saturday. */
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Minimal shape of a time entry needed to compute weekly progress. */
export interface WeeklyGoalBlock {
  start_time: string | null;
  end_time: string | null;
  /** Soft-deleted blocks never count toward the goal. */
  deleted_at?: string | null;
}

export interface WeeklyGoalProgress {
  /** The (clamped) target for the week, in hours. */
  goalHours: number;
  /** Seconds tracked so far this week (clipped to the week window and to `now`). */
  trackedSeconds: number;
  /** Decimal hours tracked so far this week. */
  trackedHours: number;
  /** trackedHours / goalHours (0 when the goal is 0 — never divides by zero). */
  progressRatio: number;
  /** Integer 0..100 for the progress bar (capped at 100 even when over goal). */
  progressPercent: number;
  /** Hours still needed to hit the goal (0 once met). */
  remainingHours: number;
  /** True once the goal is set (>0) and fully tracked. */
  isMet: boolean;
  /** Start of the week window (UTC, inclusive), ISO string. */
  weekStart: string;
  /** End of the week window (UTC, exclusive), ISO string. */
  weekEnd: string;
  /** Whole days into the week including today (1..7). */
  daysElapsed: number;
  /** Whole days left in the week including today (1..7). */
  daysRemaining: number;
  /** Even hours/day needed over the remaining days to still hit the goal. */
  requiredDailyHours: number;
  /** True when tracked hours keep up with a linear pace toward the goal. */
  onTrack: boolean;
}

/** Clamp any input to a valid goal in [0, MAX_WEEKLY_GOAL_HOURS]; NaN → 0. */
export function clampWeeklyGoalHours(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return value > MAX_WEEKLY_GOAL_HOURS ? MAX_WEEKLY_GOAL_HOURS : value;
}

/** Parse a stored/user string into a clamped goal (invalid/empty → 0). */
export function parseWeeklyGoalHours(raw: string | null | undefined): number {
  if (raw === null || raw === undefined || raw.trim() === "") {
    return 0;
  }
  return clampWeeklyGoalHours(Number.parseFloat(raw));
}

/** UTC start-of-week instant for `now` (default Monday 00:00:00.000 UTC). */
export function startOfWeekUtc(now: Date, weekStartsOn: WeekStartsOn = 1): Date {
  const d = new Date(now);
  const day = d.getUTCDay();
  const diff = (day - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  d.setUTCDate(d.getUTCDate() - diff);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

interface ComputeArgs {
  blocks: WeeklyGoalBlock[];
  goalHours: number;
  now?: Date;
  weekStartsOn?: WeekStartsOn;
}

/**
 * Overlap (ms) of a single block with the [weekStart, upperBound) window, where
 * `upperBound = min(weekEnd, now)`. A running block (no `end_time`) is treated as
 * ending at `now`. Invalid/reversed/deleted blocks contribute 0.
 */
function blockOverlapMs(
  block: WeeklyGoalBlock,
  weekStartMs: number,
  upperBoundMs: number,
  nowMs: number,
): number {
  if (block.deleted_at) {
    return 0;
  }
  if (!block.start_time) {
    return 0;
  }
  const startMs = Date.parse(block.start_time);
  if (Number.isNaN(startMs)) {
    return 0;
  }
  let endMs: number;
  if (block.end_time) {
    endMs = Date.parse(block.end_time);
    if (Number.isNaN(endMs)) {
      return 0;
    }
  } else {
    endMs = nowMs;
  }
  if (endMs <= startMs) {
    return 0;
  }
  const clippedStart = Math.max(startMs, weekStartMs);
  const clippedEnd = Math.min(endMs, upperBoundMs);
  return Math.max(0, clippedEnd - clippedStart);
}

/**
 * Snapshot of weekly-goal progress for the current week. Pure: given the same
 * inputs it always returns the same result.
 */
export function computeWeeklyGoalProgress(args: ComputeArgs): WeeklyGoalProgress {
  const now = args.now ?? new Date();
  const nowMs = now.getTime();
  const goalHours = clampWeeklyGoalHours(args.goalHours);

  const weekStart = startOfWeekUtc(now, args.weekStartsOn ?? 1);
  const weekStartMs = weekStart.getTime();
  const weekEndMs = weekStartMs + DAYS_PER_WEEK * MS_PER_DAY;
  const weekEnd = new Date(weekEndMs);
  const upperBoundMs = Math.min(weekEndMs, nowMs);

  let trackedMs = 0;
  for (const block of args.blocks) {
    trackedMs += blockOverlapMs(block, weekStartMs, upperBoundMs, nowMs);
  }

  const trackedSeconds = trackedMs / MS_PER_SECOND;
  const trackedHours = trackedMs / MS_PER_HOUR;

  const progressRatio = goalHours > 0 ? trackedHours / goalHours : 0;
  const progressPercent = Math.min(
    PERCENT_MAX,
    Math.max(0, Math.round(progressRatio * PERCENT_MAX)),
  );
  const remainingHours = Math.max(0, goalHours - trackedHours);
  const isMet = goalHours > 0 && trackedHours >= goalHours;

  // Days into the week (clip `now` to the window so a call exactly at weekEnd
  // still reports a full 7 days rather than 8).
  const elapsedMs = Math.min(Math.max(0, nowMs - weekStartMs), DAYS_PER_WEEK * MS_PER_DAY - 1);
  const daysElapsed = Math.floor(elapsedMs / MS_PER_DAY) + 1;
  const daysRemaining = DAYS_PER_WEEK - daysElapsed + 1;
  const requiredDailyHours = daysRemaining > 0 ? remainingHours / daysRemaining : remainingHours;

  const expectedByNow = (goalHours * daysElapsed) / DAYS_PER_WEEK;
  const onTrack = goalHours <= 0 || trackedHours >= expectedByNow;

  return {
    goalHours,
    trackedSeconds,
    trackedHours,
    progressRatio,
    progressPercent,
    remainingHours,
    isMet,
    weekStart: weekStart.toISOString(),
    weekEnd: weekEnd.toISOString(),
    daysElapsed,
    daysRemaining,
    requiredDailyHours,
    onTrack,
  };
}
